import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { So5RankingRewards } from 'components/ranking/So5RankingRewards';
import {
  ContenderLeftFooterState,
  getContenderLeftFooterState,
  getSecondaryContenderFooterState,
} from 'lib/contender';

import type { SimpleContenderFooterStatus_so5LeaderboardContender } from './__generated__/index.graphql';

const messages = defineMessages({
  scoreToTarget: {
    id: 'Lineup.LiveLineupFooter.scoreToTarget2',
    defaultMessage: '<b>{score}</b> to target',
  },
  scoreToClosestTarget: {
    id: 'Lineup.LiveLineupFooter.RightLiveLineupFooter.scoreToTarget2',
    defaultMessage: '<b>{score}</b> to target',
  },
  scoreToClosestTargetNumber: {
    id: 'Lineup.LiveLineupFooter.RightLiveLineupFooter.scoreToTargetNumber',
    defaultMessage: '<b>{score}</b> to target {number}',
  },
  scoreToClosestReward: {
    id: 'Lineup.LiveLineupFooter.scoreToReward2',
    defaultMessage: '<b>{score}</b> to reward',
  },
});

const StyledBold = styled.b`
  color: var(--c-white);
`;

type Props = {
  so5LeaderboardContender: SimpleContenderFooterStatus_so5LeaderboardContender;
  variant?: 'PRIMARY' | 'SECONDARY';
};

export const SimpleContenderFooterStatus = ({
  so5LeaderboardContender,
  variant = 'PRIMARY',
}: Props) => {
  const { so5Lineup } = so5LeaderboardContender;
  const so5Ranking = so5Lineup?.so5Rankings.at(0);
  const leftState =
    variant === 'SECONDARY'
      ? getSecondaryContenderFooterState(so5LeaderboardContender)
      : getContenderLeftFooterState(so5LeaderboardContender);
  if (leftState === null || !so5Ranking) {
    return null;
  }

  const { state } = leftState;

  switch (state) {
    case ContenderLeftFooterState.CANCELLED:
      return <Ellipsis>{so5Lineup?.cancelledReason}</Ellipsis>;
    case ContenderLeftFooterState.NOT_STARTED:
      return (
        <FormattedMessage
          id="Lineup.LiveLineupFooter.timeLeftToNextMatch"
          defaultMessage="<b>{timeLeft}</b> to next match"
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            timeLeft: <TimeLeft time={leftState.date} />,
          }}
        />
      );
    case ContenderLeftFooterState.REWARDS:
      return (
        so5Lineup && (
          <LinkOther as="div">
            <Horizontal gap={0.5}>
              <So5RankingRewards so5Lineup={so5Lineup} />
            </Horizontal>
          </LinkOther>
        )
      );
    case ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD:
      return (
        <FormattedMessage
          {...messages.scoreToClosestReward}
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_SAFETY:
      return (
        <FormattedMessage
          id="Lineup.LiveLineupFooter.scoreToSafety"
          defaultMessage="<b>{score}</b> to safety"
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  b: StyledBold,
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_TARGET:
      return (
        <FormattedMessage
          {...messages.scoreToTarget}
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  b: StyledBold,
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_THRESHOLD:
      return (
        <FormattedMessage
          {...(leftState.thresholdNumber > 1
            ? messages.scoreToClosestTargetNumber
            : messages.scoreToClosestTarget)}
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            number: leftState.thresholdNumber,
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  b: StyledBold,
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    default:
      return null;
  }
};

SimpleContenderFooterStatus.fragments = {
  so5LeaderboardContender: gql`
    fragment SimpleContenderFooterStatus_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        rewardMultiplier
        cancelledReason
        so5Rankings {
          id
        }
        ...So5RankingRewards_so5Lineup
      }
      ...getContenderLeftFooterState_so5LeaderboardContender
      ...getSecondaryContenderFooterState_so5LeaderboardContender
    }
    ${getContenderLeftFooterState.fragments.so5LeaderboardContender}
    ${getSecondaryContenderFooterState.fragments.so5LeaderboardContender}
    ${So5RankingRewards.fragments.so5Lineup}
  ` as TypedDocumentNode<SimpleContenderFooterStatus_so5LeaderboardContender>,
};
